import { APP_LANGUAGES, getLanguageOptionLabel } from "../../utils/languages";
import ThemeToggleButton from "../common/ThemeToggleButton";
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { api } from "../../services/api";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { getAppLanguage, useAppLanguage } from "../../utils/appLanguage";

export const REGION_OPTIONS = [
  { value: "healjai", label: "Thailand" },
  { value: "indonesia", label: "Indonesia" },
  { value: "canada", label: "Canada" },
  { value: "uk", label: "UK" },
  { value: "gcc", label: "GCC" },
  { value: "malaysia", label: "Malaysia" },
  { value: "singapore", label: "Singapore" },
  { value: "philippines", label: "Philippines" },
  { value: "vietnam", label: "Vietnam" },
  { value: "mexico", label: "Mexico" },
  { value: "brazil", label: "Brazil" },
  { value: "korea", label: "Korea" },
  { value: "india", label: "India" },
  { value: "japan", label: "Japan" },
  { value: "spanish", label: "Spanish" },
  { value: "us", label: "US" },
  { value: "italy", label: "Italy" },
  { value: "germany", label: "Germany" },
  { value: "china", label: "China" },
  { value: "russia", label: "Russia" },
  { value: "france", label: "France" },
];

const Register: React.FC = () => {
  // The landing page profile form can hand over the visitor's first name.
  const location = useLocation();
  const prefillUsername = (location.state as { prefillUsername?: string } | null)
    ?.prefillUsername;
  const [username, setUsername] = useState(prefillUsername ?? "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [mobileNo, setMobileNo] = useState("");
  const [roleId, setRoleId] = useState("2");
  // Starts in the language the visitor is already reading (e.g. the one picked
  // on the landing page); picking another switches this form right away.
  const [preferredLanguage, setPreferredLanguage] = useState(getAppLanguage);
  const [region, setRegion] = useState("healjai");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();
  const { register } = useAuth();
  const { t, i18n } = useTranslation();
  const uiLanguage = useAppLanguage();

  const handleLanguageChange = (code: string) => {
    setPreferredLanguage(code);
    i18n.changeLanguage(code).catch(() => {
      // keep the current language if the switch fails
    });
  };

  // Auto-detect region from IP on mount
  useEffect(() => {
    api
      .get("/detect-region")
      .then((res) => {
        if (res.data?.region) {
          setRegion(res.data.region);
        }
      })
      .catch(() => {
        // silently fall back to healjai default
      });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!username.trim()) {
      toast.error(t("auth.usernameRequired"));
      return;
    }

    if (!email.trim()) {
      toast.error(t("auth.emailRequired"));
      return;
    }

    if (!password.trim()) {
      toast.error(t("auth.passwordRequired"));
      return;
    }

    if (password !== confirmPassword) {
      toast.error(t("auth.passwordsDontMatch"));
      return;
    }

    if (password.length < 6) {
      toast.error(t("auth.passwordTooShort"));
      return;
    }

    setIsLoading(true);

    try {
      await register(
        username,
        email,
        password,
        mobileNo || undefined,
        preferredLanguage,
        roleId,
        region,
      );
      // AuthContext shows the success toast.
      navigate("/");
    } catch (error: any) {
      console.error("Registration error in component:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getPasswordStrength = (password: string) => {
    if (password.length === 0) return { strength: 0, text: "", color: "" };
    if (password.length < 6)
      return { strength: 25, text: t("auth.strengthWeak"), color: "bg-red-500" };
    if (password.length < 8)
      return { strength: 50, text: t("auth.strengthFair"), color: "bg-yellow-500" };
    if (password.length < 10)
      return { strength: 75, text: t("auth.strengthGood"), color: "bg-blue-500" };
    return { strength: 100, text: t("auth.strengthStrong"), color: "bg-green-500" };
  };

  const passwordStrength = getPasswordStrength(password);

  const dropdownStyle = {
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236B7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
    backgroundPosition: "right 0.75rem center",
    backgroundRepeat: "no-repeat",
    backgroundSize: "1.5em 1.5em",
    paddingRight: "2.5rem",
  };

  return (
    <div
      className="astria-themable astria-auth min-h-screen flex items-center justify-center px-4 py-8 relative overflow-hidden"
      style={{ background: "radial-gradient(circle at top, var(--astria-accent-glow), transparent 60%), var(--astria-bg-app)" }}
    >
      <ThemeToggleButton className="absolute right-4 top-4 z-20" />
      <div
        className="relative z-10 w-full max-w-md rounded-[2.8rem] p-8"
        style={{
          backgroundColor: "#1A131F",
          border: "1px solid rgba(255,255,255,0.08)",
          boxShadow: "var(--astria-shadow-card)",
        }}
      >
        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-center text-[#F7F3FA] mb-2">{t("auth.createAccount")}</h2>
          <p className="text-center text-sm text-[#C8BFD1]">
            {t("auth.haveAccount")}{" "}
            <Link to="/login" className="text-[#A78BFF] font-medium underline">
              {t("auth.signIn")}
            </Link>
          </p>
        </div>

        {/* Form */}
        <form className="space-y-5" onSubmit={handleSubmit}>
          {/* Username */}
          <div>
            <label className="block text-sm text-[#C8BFD1] mb-1">
              {t("auth.username")} *
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full rounded-xl border p-3 text-sm astria-auth-input bg-[#221A29] text-[#F7F3FA] border-white/10 placeholder:text-[#C8BFD1]/70 outline-none focus:ring-2 focus:ring-[#A78BFF]/40"
              placeholder={t("auth.enterUsername")}
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm text-[#C8BFD1] mb-1">{t("auth.email")} *</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-xl border p-3 text-sm astria-auth-input bg-[#221A29] text-[#F7F3FA] border-white/10 placeholder:text-[#C8BFD1]/70 outline-none focus:ring-2 focus:ring-[#A78BFF]/40"
              placeholder="you@example.com"
            />
          </div>

          {/* Mobile */}
          <div>
            <label className="block text-sm text-[#C8BFD1] mb-1">
              {t("auth.mobileOptional")}
            </label>
            <input
              type="tel"
              value={mobileNo}
              onChange={(e) => setMobileNo(e.target.value)}
              className="w-full rounded-xl border p-3 text-sm astria-auth-input bg-[#221A29] text-[#F7F3FA] border-white/10 placeholder:text-[#C8BFD1]/70 outline-none focus:ring-2 focus:ring-[#A78BFF]/40"
              placeholder="+1 234 567 8900"
            />
          </div>

          {/* Region Dropdown */}
          <div>
            <label className="block text-sm text-[#C8BFD1] mb-1">{t("auth.region")} *</label>
            <select
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              className="w-full rounded-xl border p-3 text-sm astria-auth-input bg-[#221A29] text-[#F7F3FA] border-white/10 placeholder:text-[#C8BFD1]/70 outline-none focus:ring-2 focus:ring-[#A78BFF]/40 appearance-none cursor-pointer disabled:cursor-not-allowed disabled:opacity-70"
              style={dropdownStyle}
            >
              {REGION_OPTIONS.map((opt) => (
                <option
                  key={opt.value}
                  value={opt.value}
                  className="bg-[#221A29] text-[#F7F3FA]"
                >
                  {t(`astriaHome.regionName.${opt.value}`, { defaultValue: opt.label })}
                </option>
              ))}
            </select>
            <p className="text-xs text-[#C8BFD1] mt-1">
              {t("auth.regionHint")}
            </p>
          </div>

          {/* Preferred Language Dropdown */}
          <div>
            <label className="block text-sm text-[#C8BFD1] mb-1">
              {t("auth.preferredLanguage")} *
            </label>
            <select
              value={preferredLanguage}
              onChange={(e) => handleLanguageChange(e.target.value)}
              className="w-full rounded-xl border p-3 text-sm astria-auth-input bg-[#221A29] text-[#F7F3FA] border-white/10 placeholder:text-[#C8BFD1]/70 outline-none focus:ring-2 focus:ring-[#A78BFF]/40 appearance-none cursor-pointer"
              style={dropdownStyle}
            >
              {APP_LANGUAGES.map((language) => (
                <option
                  key={language.code}
                  value={language.code}
                  className="bg-[#221A29] text-[#F7F3FA]"
                >
                  {getLanguageOptionLabel(language, uiLanguage)}
                </option>
              ))}
            </select>
            <p className="text-xs text-[#C8BFD1] mt-1">
              {t("auth.languageHint")}
            </p>
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm text-[#C8BFD1] mb-1">
              {t("auth.password")} *
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-xl border p-3 text-sm astria-auth-input bg-[#221A29] text-[#F7F3FA] border-white/10 placeholder:text-[#C8BFD1]/70 outline-none focus:ring-2 focus:ring-[#A78BFF]/40"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-3 text-[#C8BFD1] hover:text-[#F7F3FA]"
              >
                {showPassword ? "🙈" : "👁️"}
              </button>
            </div>

            {password.length > 0 && (
              <div className="mt-2">
                <div className="flex justify-between text-xs text-[#C8BFD1] mb-1">
                  <span>{t("auth.passwordStrength")}</span>
                  <span>{passwordStrength.text}</span>
                </div>
                <div className="w-full h-1.5 bg-gray-700 rounded-full">
                  <div
                    className={`${passwordStrength.color} h-1.5 rounded-full transition-all`}
                    style={{ width: `${passwordStrength.strength}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm text-[#C8BFD1] mb-1">
              {t("auth.confirmPassword")} *
            </label>
            <input
              type={showPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full rounded-xl border p-3 text-sm astria-auth-input bg-[#221A29] text-[#F7F3FA] border-white/10 placeholder:text-[#C8BFD1]/70 outline-none focus:ring-2 focus:ring-[#A78BFF]/40"
              placeholder="••••••••"
            />
            {confirmPassword && password !== confirmPassword && (
              <p className="text-xs text-red-400 mt-1">
                {t("auth.passwordsDontMatch")}
              </p>
            )}
          </div>

          <input type="hidden" value={roleId} />

          {/* Submit */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full rounded-xl py-3 font-semibold bg-[#A78BFF] text-[#0F0A14] shadow-[0_0_22px_rgba(167,139,255,0.35)] hover:brightness-110 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? t("auth.creatingAccount") : t("auth.createAccount")}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Register;
